import React, { useState, useMemo } from 'react';
import type { TariffService, TariffParking, TariffWater, Role, TariffCollection, OtherFeeConfig, Unit } from '../../types';
import Modal from '../ui/Modal';
import { useNotification } from '../../App';
import { PencilSquareIcon, TagIcon, CarIcon, DropletsIcon, PlusIcon, TrashIcon, InformationCircleIcon } from '../ui/Icons';
import StatCard from '../ui/StatCard';
import { formatCurrency } from '../../utils/helpers';
import { DEFAULT_PARKING_TARIFFS } from '../../constants';
import { ParkingTariffTier } from '../../types';

interface PricingPageProps {
    tariffs: TariffCollection;
    setTariffs: (updater: React.SetStateAction<TariffCollection>, logPayload?: any) => void;
    role: Role;
    otherFeeConfigs: OtherFeeConfig[];
    units: Unit[];
    onSaveOtherFee: (config: OtherFeeConfig) => Promise<void>;
    onDeleteOtherFee: (id: string) => Promise<void>;
}

const OtherFeeForm: React.FC<{ 
    item?: OtherFeeConfig; 
    units: Unit[];
    onSave: (data: OtherFeeConfig) => void; 
    onCancel: () => void 
}> = ({ item, units, onSave, onCancel }) => {
    const [formData, setFormData] = useState<OtherFeeConfig>(item || {
        id: `fee_${Date.now()}`,
        name: '',
        description: '',
        unitPrice: 0,
        type: 'per_unit',
        frequency: 'monthly',
        targetType: 'all',
        targetUnitIds: [],
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        let parsedValue: any = value;
        if (type === 'number') parsedValue = value === '' ? '' : parseFloat(value);
        if (name === 'isActive') parsedValue = (e.target as HTMLInputElement).checked;
        
        setFormData({ ...formData, [name]: parsedValue });
    };

    const handleUnitToggle = (unitId: string) => {
        const current = formData.targetUnitIds || [];
        const next = current.includes(unitId) 
            ? current.filter(id => id !== unitId)
            : [...current, unitId];
        setFormData({ ...formData, targetUnitIds: next });
    };

    const inputStyle = "w-full p-2 border rounded-md bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent outline-none";
    const labelStyle = "block text-sm font-medium text-gray-700 mb-1";

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                    <label className={labelStyle}>Tên khoản phí</label>
                    <input name="name" value={formData.name} onChange={handleChange} className={inputStyle} required placeholder="Ví dụ: Phí bảo trì thang máy" />
                </div>
                <div className="md:col-span-2">
                    <label className={labelStyle}>Mô tả</label>
                    <textarea name="description" value={formData.description} onChange={handleChange} className={inputStyle} rows={2} />
                </div>
                <div>
                    <label className={labelStyle}>Đơn giá</label>
                    <input type="number" name="unitPrice" value={formData.unitPrice} onChange={handleChange} className={inputStyle} required />
                </div>
                <div>
                    <label className={labelStyle}>Cách tính</label>
                    <select name="type" value={formData.type} onChange={handleChange} className={inputStyle}>
                        <option value="per_unit">Cố định mỗi căn hộ</option>
                        <option value="per_m2">Theo diện tích (m²)</option>
                    </select>
                </div>
                <div>
                    <label className={labelStyle}>Tần suất</label>
                    <select name="frequency" value={formData.frequency} onChange={handleChange} className={inputStyle}>
                        <option value="monthly">Hàng tháng</option>
                        <option value="once">Thu một lần</option>
                    </select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                    <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} id="isActive" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                    <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Đang hoạt động</label>
                </div>
                <div className="md:col-span-2">
                    <label className={labelStyle}>Áp dụng cho</label>
                    <div className="flex gap-4 mb-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" name="targetType" value="all" checked={formData.targetType === 'all'} onChange={handleChange} className="text-primary" />
                            <span className="text-sm">Tất cả căn hộ</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" name="targetType" value="specific" checked={formData.targetType === 'specific'} onChange={handleChange} className="text-primary" />
                            <span className="text-sm">Căn hộ cụ thể</span>
                        </label>
                    </div>
                    
                    {formData.targetType === 'specific' && (
                        <div className="border rounded-md p-3 bg-gray-50 max-h-40 overflow-y-auto">
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                                {units.map(u => (
                                    <button 
                                        key={u.UnitID}
                                        type="button"
                                        onClick={() => handleUnitToggle(u.UnitID)}
                                        className={`px-2 py-1 text-xs rounded border transition-colors ${formData.targetUnitIds.includes(u.UnitID) ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-primary'}`}
                                    >
                                        {u.UnitID}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">Hủy</button>
                <button type="submit" className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-focus font-semibold">Lưu cấu hình</button>
            </div>
        </form>
    );
};

const EditForm: React.FC<{ item: any; onSave: (data: any) => void; onCancel: () => void }> = ({ item, onSave, onCancel }) => {
    const [formData, setFormData] = useState(item);
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const parsedValue = type === 'number' ? (value === '' ? '' : parseFloat(value)) : value;
        setFormData({ ...formData, [name]: parsedValue });
    };

    const inputStyle = "w-full p-2 border rounded-md bg-white border-gray-300 text-gray-900 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed focus:ring-2 focus:ring-primary focus:border-transparent outline-none";
    const labelStyle = "block text-sm font-medium text-gray-700 mb-1 capitalize";

    const renderFields = () => {
        // Determine type based on keys
        if ('ServiceFee_per_m2' in formData) { // Service
            return <>
                <div><label className={labelStyle}>Loại hình</label><input value={formData.LoaiHinh} className={inputStyle} disabled /></div>
                <div><label className={labelStyle}>Đơn giá / m²</label><input type="number" name="ServiceFee_per_m2" value={formData.ServiceFee_per_m2} onChange={handleChange} className={inputStyle} /></div>
                <div><label className={labelStyle}>VAT %</label><input type="number" name="VAT_percent" value={formData.VAT_percent} onChange={handleChange} className={inputStyle} /></div>
                <div><label className={labelStyle}>Ngày hết hạn (YYYY-MM-DD)</label><input type="text" name="ValidTo" value={formData.ValidTo || ''} onChange={handleChange} className={inputStyle} placeholder="Để trống nếu đang áp dụng" /></div>
            </>;
        }
        if ('Price_per_unit' in formData) { // Parking
            return <>
                <div><label className={labelStyle}>Loại xe / Bậc</label><input value={formData.Tier} className={inputStyle} disabled /></div>
                <div><label className={labelStyle}>Đơn giá</label><input type="number" name="Price_per_unit" value={formData.Price_per_unit} onChange={handleChange} className={inputStyle} /></div>
                <div><label className={labelStyle}>VAT %</label><input type="number" name="VAT_percent" value={formData.VAT_percent} onChange={handleChange} className={inputStyle} /></div>
                <div><label className={labelStyle}>Ngày hết hạn (YYYY-MM-DD)</label><input type="text" name="ValidTo" value={formData.ValidTo || ''} onChange={handleChange} className={inputStyle} placeholder="Để trống nếu đang áp dụng" /></div>
            </>;
        }
         if ('UnitPrice' in formData) { // Water
            return <>
                <div><label className={labelStyle}>Từ (m³)</label><input value={formData.From_m3} className={inputStyle} disabled /></div>
                <div><label className={labelStyle}>Đến (m³)</label><input type="number" name="To_m3" value={formData.To_m3 ?? ''} onChange={handleChange} className={inputStyle} placeholder="Để trống cho bậc cuối"/></div>
                <div><label className={labelStyle}>Đơn giá</label><input type="number" name="UnitPrice" value={formData.UnitPrice} onChange={handleChange} className={inputStyle} /></div>
                <div><label className={labelStyle}>VAT %</label><input type="number" name="VAT_percent" value={formData.VAT_percent} onChange={handleChange} className={inputStyle} /></div>
                <div className="md:col-span-2"><label className={labelStyle}>Ngày hết hạn (YYYY-MM-DD)</label><input type="text" name="ValidTo" value={formData.ValidTo || ''} onChange={handleChange} className={inputStyle} placeholder="Để trống nếu đang áp dụng" /></div>
            </>
        }
        return <p>Unknown tariff type</p>;
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
               {renderFields()}
            </div>
            <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium">Hủy</button>
                <button type="submit" className="px-4 py-2 bg-primary text-white font-semibold rounded-md shadow-sm hover:bg-primary-focus">Lưu thay đổi</button>
            </div>
        </form>
    );
};


const PricingPage: React.FC<PricingPageProps> = ({ tariffs, setTariffs, role, otherFeeConfigs, units, onSaveOtherFee, onDeleteOtherFee }) => {
    const { showToast } = useNotification();
    const canEdit = useMemo(() => role === 'Admin', [role]);

    const [activeTab, setActiveTab] = useState<'service' | 'parking' | 'water' | 'other'>('service');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<{ type: 'service' | 'parking' | 'water' | 'other'; data: any } | null>(null);

    const handleEdit = (type: 'service' | 'parking' | 'water' | 'other', data: any) => {
        if (!canEdit) {
            showToast('Bạn không có quyền chỉnh sửa.', 'error');
            return;
        }
        setEditingItem({ type, data });
        setIsModalOpen(true);
    };

    const handleInitializeParkingTariffs = () => {
        if (!canEdit) return;
        
        const existingTiers = new Set(tariffs.parking.map(t => t.Tier.toLowerCase()));
        const missingDefaults = DEFAULT_PARKING_TARIFFS.filter(d => !existingTiers.has(d.Tier.toLowerCase()));
        
        if (missingDefaults.length === 0) {
            showToast('Tất cả các loại xe đã có cấu hình đơn giá.', 'info');
            return;
        }

        const newTariffs = {
            ...tariffs,
            parking: [...tariffs.parking, ...missingDefaults]
        };

        setTariffs(newTariffs, {
            module: 'Pricing',
            action: 'INITIALIZE_PARKING_TARIFFS',
            summary: `Khởi tạo ${missingDefaults.length} đơn giá gửi xe còn thiếu.`,
            before_snapshot: tariffs,
        });
        showToast(`Đã khởi tạo ${missingDefaults.length} đơn giá gửi xe còn thiếu.`, 'success');
    };

    const handleAddNewOtherFee = () => {
        if (!canEdit) return;
        setEditingItem({ type: 'other', data: null });
        setIsModalOpen(true);
    };

    const handleDeleteOtherFeeClick = async (id: string) => {
        if (!canEdit) return;
        if (window.confirm('Bạn có chắc chắn muốn xóa cấu hình phí này?')) {
            try {
                await onDeleteOtherFee(id);
                showToast('Đã xóa cấu hình phí.', 'success');
            } catch (e) {
                showToast('Lỗi khi xóa cấu hình phí.', 'error');
            }
        }
    };

    const handleSave = async (updatedData: any) => {
        if (!editingItem) return;
        const { type } = editingItem;
        
        if (type === 'other') {
            try {
                await onSaveOtherFee(updatedData);
                showToast('Lưu cấu hình phí thành công!', 'success');
                setIsModalOpen(false);
                setEditingItem(null);
            } catch (e) {
                showToast('Lỗi khi lưu cấu hình phí.', 'error');
            }
            return;
        }

        const newTariffs = JSON.parse(JSON.stringify(tariffs));
        let index = -1;
        
        if (type === 'service') {
            index = newTariffs.service.findIndex((t: TariffService) => t.LoaiHinh === updatedData.LoaiHinh);
            if (index > -1) newTariffs.service[index] = updatedData;
        } else if (type === 'parking') {
            index = newTariffs.parking.findIndex((t: TariffParking) => t.Tier === updatedData.Tier);
            if (index > -1) newTariffs.parking[index] = updatedData;
        } else if (type === 'water') {
            index = newTariffs.water.findIndex((t: TariffWater) => t.From_m3 === updatedData.From_m3);
            if (index > -1) newTariffs.water[index] = updatedData;
        }
        
        setTariffs(newTariffs, {
            module: 'Pricing',
            action: 'UPDATE_TARIFF',
            summary: `Cập nhật đơn giá cho ${type}: ${updatedData.LoaiHinh || updatedData.Tier || `Bậc ${updatedData.From_m3}`}`,
            before_snapshot: tariffs,
        });
        showToast('Cập nhật đơn giá thành công!', 'success');
        setIsModalOpen(false);
        setEditingItem(null);
    };

    const formatDate = (dateStr: string | null) => dateStr ? new Date(dateStr).toLocaleDateString('vi-VN') : null;

    const TabButton: React.FC<{ tabId: 'service' | 'parking' | 'water' | 'other', label: string, icon: React.ReactNode }> = ({ tabId, label, icon }) => (
        <button 
            type="button" 
            onClick={() => setActiveTab(tabId)} 
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors duration-200 ${activeTab === tabId ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            role="tab"
            aria-selected={activeTab === tabId}
        >
            {icon} {label}
        </button>
    );

    const tableHeaderClass = "px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider";
    const tableRowClass = "hover:bg-gray-50 text-sm text-gray-800";
    const tableCellClass = "px-4 py-3";

    return (
        <div className="space-y-6">
            {isModalOpen && editingItem && (
                <Modal title={editingItem.type === 'other' ? (editingItem.data ? 'Sửa cấu hình phí' : 'Thêm cấu hình phí mới') : `Chỉnh sửa Đơn giá ${editingItem.type.charAt(0).toUpperCase() + editingItem.type.slice(1)}`} onClose={() => setIsModalOpen(false)}>
                    {editingItem.type === 'other' ? (
                        <OtherFeeForm item={editingItem.data} units={units} onSave={handleSave} onCancel={() => setIsModalOpen(false)} />
                    ) : (
                        <EditForm item={editingItem.data} onSave={handleSave} onCancel={() => setIsModalOpen(false)} />
                    )}
                </Modal>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                 <StatCard 
                    label="Biểu phí Dịch vụ" 
                    value={`${tariffs.service.length} mục`} 
                    icon={<TagIcon className="w-6 h-6 text-blue-600"/>} 
                    iconBgClass="bg-blue-100" 
                    className="border-l-4 border-blue-500"
                    onClick={() => setActiveTab('service')}
                />
                <StatCard 
                    label="Biểu phí Gửi xe" 
                    value={`${tariffs.parking.length} mục`} 
                    icon={<CarIcon className="w-6 h-6 text-orange-600"/>} 
                    iconBgClass="bg-orange-100" 
                    className="border-l-4 border-orange-500"
                    onClick={() => setActiveTab('parking')}
                />
                <StatCard 
                    label="Biểu phí Nước" 
                    value={`${tariffs.water.length} mục`} 
                    icon={<DropletsIcon className="w-6 h-6 text-cyan-600"/>} 
                    iconBgClass="bg-cyan-100" 
                    className="border-l-4 border-cyan-500"
                    onClick={() => setActiveTab('water')}
                />
                <StatCard 
                    label="Phí khác" 
                    value={`${otherFeeConfigs.length} mục`} 
                    icon={<PlusIcon className="w-6 h-6 text-purple-600"/>} 
                    iconBgClass="bg-purple-100" 
                    className="border-l-4 border-purple-500"
                    onClick={() => setActiveTab('other')}
                />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="border-b border-gray-200">
                    <nav className="flex -mb-px px-6" role="tablist" aria-label="Bảng giá">
                        <TabButton tabId="service" label="Dịch vụ" icon={<TagIcon className="w-4 h-4" />}/>
                        <TabButton tabId="parking" label="Gửi xe" icon={<CarIcon className="w-4 h-4" />}/>
                        <TabButton tabId="water" label="Nước" icon={<DropletsIcon className="w-4 h-4" />}/>
                        <TabButton tabId="other" label="Phí khác" icon={<PlusIcon className="w-4 h-4" />}/>
                    </nav>
                </div>
                
                <div className="p-6">
                    {activeTab === 'service' && (
                        <div className="overflow-x-auto" role="tabpanel">
                            <table className="min-w-full">
                                <thead><tr>
                                    <th className={tableHeaderClass}>Loại hình</th>
                                    <th className={`${tableHeaderClass} text-right`}>Đơn giá / m²</th>
                                    <th className={`${tableHeaderClass} text-center`}>VAT</th>
                                    <th className={`${tableHeaderClass} text-center`}>Trạng thái</th>
                                    <th className={`${tableHeaderClass} text-center`}>Hành động</th>
                                </tr></thead>
                                <tbody className="divide-y divide-gray-100">
                                {tariffs.service.map((item) => (
                                    <tr key={item.LoaiHinh} className={tableRowClass}>
                                        <td className={`${tableCellClass} font-semibold`}>{item.LoaiHinh}</td>
                                        <td className={`${tableCellClass} text-right font-bold text-primary font-mono`}>{formatCurrency(item.ServiceFee_per_m2)}</td>
                                        <td className={`${tableCellClass} text-center`}><span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-md">{item.VAT_percent}%</span></td>
                                        <td className={`${tableCellClass} text-center`}>{!item.ValidTo ? <span className="text-green-600 font-semibold text-xs">Đang áp dụng</span> : <span className="text-gray-500 text-xs">Hết hạn {formatDate(item.ValidTo)}</span>}</td>
                                        <td className={`${tableCellClass} text-center`}><button onClick={() => handleEdit('service', item)} disabled={!canEdit} className="text-blue-600 hover:text-blue-800 disabled:text-gray-300 p-1 rounded-full hover:bg-blue-50" aria-label={`Sửa ${item.LoaiHinh}`}><PencilSquareIcon/></button></td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {activeTab === 'parking' && (
                        <div className="space-y-4" role="tabpanel">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <InformationCircleIcon className="w-4 h-4" />
                                    <span>Đơn giá gửi xe được áp dụng theo từng loại phương tiện.</span>
                                </div>
                                {canEdit && (
                                    <button 
                                        onClick={handleInitializeParkingTariffs}
                                        className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-semibold transition-colors"
                                    >
                                        <PlusIcon className="w-4 h-4" /> Khởi tạo mục còn thiếu
                                    </button>
                                )}
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full">
                                <thead><tr>
                                    <th className={tableHeaderClass}>Loại xe / Bậc</th>
                                    <th className={`${tableHeaderClass} text-right`}>Đơn giá</th>
                                    <th className={`${tableHeaderClass} text-center`}>VAT</th>
                                    <th className={`${tableHeaderClass} text-center`}>Trạng thái</th>
                                    <th className={`${tableHeaderClass} text-center`}>Hành động</th>
                                </tr></thead>
                                <tbody className="divide-y divide-gray-100">
                                {tariffs.parking.map((item) => (
                                    <tr key={item.Tier} className={tableRowClass}>
                                        <td className={`${tableCellClass} font-semibold`}>{item.Tier}</td>
                                        <td className={`${tableCellClass} text-right font-bold text-primary font-mono`}>{formatCurrency(item.Price_per_unit)}</td>
                                        <td className={`${tableCellClass} text-center`}><span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-md">{item.VAT_percent}%</span></td>
                                        <td className={`${tableCellClass} text-center`}>{!item.ValidTo ? <span className="text-green-600 font-semibold text-xs">Đang áp dụng</span> : <span className="text-gray-500 text-xs">Hết hạn {formatDate(item.ValidTo)}</span>}</td>
                                        <td className={`${tableCellClass} text-center`}><button onClick={() => handleEdit('parking', item)} disabled={!canEdit} className="text-blue-600 hover:text-blue-800 disabled:text-gray-300 p-1 rounded-full hover:bg-blue-50" aria-label={`Sửa ${item.Tier}`}><PencilSquareIcon/></button></td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    )}
                    {activeTab === 'water' && (
                        <div className="overflow-x-auto" role="tabpanel">
                            <table className="min-w-full">
                                <thead><tr>
                                    <th className={`${tableHeaderClass} text-right`}>Từ (m³)</th>
                                    <th className={`${tableHeaderClass} text-right`}>Đến (m³)</th>
                                    <th className={`${tableHeaderClass} text-right`}>Đơn giá</th>
                                    <th className={`${tableHeaderClass} text-center`}>VAT</th>
                                    <th className={`${tableHeaderClass} text-center`}>Trạng thái</th>
                                    <th className={`${tableHeaderClass} text-center`}>Hành động</th>
                                </tr></thead>
                                <tbody className="divide-y divide-gray-100">
                                {tariffs.water.map((item, index) => (
                                    <tr key={index} className={tableRowClass}>
                                        <td className={`${tableCellClass} text-right`}>{item.From_m3}</td>
                                        <td className={`${tableCellClass} text-right`}>{item.To_m3 === null ? 'Trở lên' : item.To_m3}</td>
                                        <td className={`${tableCellClass} text-right font-bold text-primary font-mono`}>{formatCurrency(item.UnitPrice)}</td>
                                        <td className={`${tableCellClass} text-center`}><span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-md">{item.VAT_percent}%</span></td>
                                        <td className={`${tableCellClass} text-center`}>{!item.ValidTo ? <span className="text-green-600 font-semibold text-xs">Đang áp dụng</span> : <span className="text-gray-500 text-xs">Hết hạn {formatDate(item.ValidTo)}</span>}</td>
                                        <td className={`${tableCellClass} text-center`}><button onClick={() => handleEdit('water', item)} disabled={!canEdit} className="text-blue-600 hover:text-blue-800 disabled:text-gray-300 p-1 rounded-full hover:bg-blue-50" aria-label={`Sửa bậc ${item.From_m3}`}><PencilSquareIcon/></button></td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {activeTab === 'other' && (
                        <div className="space-y-4" role="tabpanel">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <InformationCircleIcon className="w-4 h-4" />
                                    <span>Các khoản phí này sẽ được tự động cộng vào bảng tính phí hàng tháng.</span>
                                </div>
                                {canEdit && (
                                    <button 
                                        onClick={handleAddNewOtherFee}
                                        className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-md hover:bg-primary-focus text-sm font-semibold transition-colors"
                                    >
                                        <PlusIcon className="w-4 h-4" /> Thêm phí mới
                                    </button>
                                )}
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-full">
                                    <thead><tr>
                                        <th className={tableHeaderClass}>Tên khoản phí</th>
                                        <th className={tableHeaderClass}>Cách tính</th>
                                        <th className={`${tableHeaderClass} text-right`}>Đơn giá</th>
                                        <th className={`${tableHeaderClass} text-center`}>Tần suất</th>
                                        <th className={`${tableHeaderClass} text-center`}>Đối tượng</th>
                                        <th className={`${tableHeaderClass} text-center`}>Trạng thái</th>
                                        <th className={`${tableHeaderClass} text-center`}>Hành động</th>
                                    </tr></thead>
                                    <tbody className="divide-y divide-gray-100">
                                    {otherFeeConfigs.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-8 text-center text-gray-500 italic">Chưa có cấu hình phí bổ sung nào.</td>
                                        </tr>
                                    ) : (
                                        otherFeeConfigs.map((item) => (
                                            <tr key={item.id} className={tableRowClass}>
                                                <td className={tableCellClass}>
                                                    <div className="font-semibold">{item.name}</div>
                                                    <div className="text-xs text-gray-500 truncate max-w-xs">{item.description}</div>
                                                </td>
                                                <td className={tableCellClass}>
                                                    {item.type === 'per_unit' ? 'Cố định / căn' : 'Theo m² diện tích'}
                                                </td>
                                                <td className={`${tableCellClass} text-right font-bold text-primary font-mono`}>
                                                    {formatCurrency(item.unitPrice)}
                                                </td>
                                                <td className={`${tableCellClass} text-center`}>
                                                    <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${item.frequency === 'monthly' ? 'bg-blue-50 text-blue-600' : 'bg-orange-50 text-orange-600'}`}>
                                                        {item.frequency === 'monthly' ? 'Hàng tháng' : 'Một lần'}
                                                    </span>
                                                </td>
                                                <td className={`${tableCellClass} text-center`}>
                                                    {item.targetType === 'all' ? (
                                                        <span className="text-xs text-gray-600">Tất cả ({units.length})</span>
                                                    ) : (
                                                        <span className="text-xs text-gray-600">{item.targetUnitIds.length} căn hộ</span>
                                                    )}
                                                </td>
                                                <td className={`${tableCellClass} text-center`}>
                                                    {item.isActive ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Hoạt động</span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Tạm dừng</span>
                                                    )}
                                                </td>
                                                <td className={`${tableCellClass} text-center`}>
                                                    <div className="flex justify-center gap-1">
                                                        <button onClick={() => handleEdit('other', item)} disabled={!canEdit} className="text-blue-600 hover:text-blue-800 disabled:text-gray-300 p-1 rounded-full hover:bg-blue-50" title="Sửa"><PencilSquareIcon/></button>
                                                        <button onClick={() => handleDeleteOtherFeeClick(item.id)} disabled={!canEdit} className="text-red-600 hover:text-red-800 disabled:text-gray-300 p-1 rounded-full hover:bg-red-50" title="Xóa"><TrashIcon/></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PricingPage;
